import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createCompany,
  createSecondCompany,
  fetchCompany,
  inviteUser,
  joinCompany,
  updateCompany,
  uploadLogo,
} from '~/api/company';
import { useActiveCompanyId } from '~/hooks/useActiveCompany';
import { useAnalytics } from '~/hooks/useAnalytics';
import { AnalyticsEvents } from '~/services/analytics';
import { Company, CompanyQueryKeys, CreateCompanyPayload } from '~/types/company';
import { InvoicesQueryKeys } from '~/types/invoicing';
import { UserQueryKeys } from '~/types/user';
import { UserBankAccountQueryKeys } from '~/types/user-bank-account';

export const useCompanyCreateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ values, token }: { values: CreateCompanyPayload; token?: string }) => createCompany(values, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: UserQueryKeys.currentUser });
    },
  });
};

export const useSecondCompanyCreateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createSecondCompany,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: UserQueryKeys.currentUser });
    },
  });
};

export const useCompanyJoinMutation = () => {
  return useMutation({ mutationFn: joinCompany });
};

export const useUploadLogoMutation = () => {
  const queryClient = useQueryClient();
  const { track } = useAnalytics();

  return useMutation({
    mutationFn: ({ file, companyId }: { file: File; companyId: Company['id'] }) => uploadLogo(file, companyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: UserQueryKeys.currentUser });
      queryClient.invalidateQueries({ queryKey: CompanyQueryKeys.allCompanies });
      queryClient.invalidateQueries({ queryKey: InvoicesQueryKeys.all });
      track(AnalyticsEvents.COMPANY_LOGO_UPLOADED, { context: 'document-builder' });
    },
  });
};

export const useUpdateCompany = () => {
  const { track } = useAnalytics();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: Partial<Company> & { id: Company['id'] }) => updateCompany(values.id, values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: UserQueryKeys.currentUser });
      queryClient.invalidateQueries({ queryKey: CompanyQueryKeys.allCompanies });
      queryClient.invalidateQueries({ queryKey: InvoicesQueryKeys.all });
      track(AnalyticsEvents.COMPANY_DATA_UPDATED, { context: 'document-builder' });
    },
  });
};

export const useInviteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: inviteUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CompanyQueryKeys.allCompanyUsers });
      queryClient.invalidateQueries({ queryKey: CompanyQueryKeys.allCompanies });
      queryClient.invalidateQueries({ queryKey: UserBankAccountQueryKeys.all });
    },
  });
};

export const useHasDocuments = () => {
  const companyId = useActiveCompanyId();

  return useQuery({
    queryKey: CompanyQueryKeys.company(companyId),
    queryFn: () => fetchCompany(companyId),
    enabled: !!companyId,
    select: ({ hasDocuments }) => hasDocuments,
    staleTime: Infinity,
  });
};
